import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalState, Selection, SelectionResolver } from '@leverege/ui-redux';
import { Attributes, DataSources } from '@leverege/ui-attributes';
import bbox from '@turf/bbox';
import distance from '@turf/distance';
import { point } from '@turf/helpers';
import centerOfMass from '@turf/center-of-mass';
import { MapControl } from '@leverege/ui-mapbox-elements';
import { ItemDelegate, Relationship, GroupDelegate } from '@leverege/molten-core';
import FilterSourceModel from "../../../../../filters/FilterSourceModel.js";
import { FilterSourcesModel } from "../../../../../filters/index.js";
import PromiseQueue from "../PromiseQueue.js";
import Util from "../Util.js";
import Model from "../GroupClusterModel.js";
const MISSING_VALUE = 'N/A';
const DEFAULT_FILTER_MODEL = FilterSourceModel.create();
export default function useGroupClusterData(props) {
  const {
    attribute,
    delegate,
    filter,
    filters: pFilters,
    onFilterChange,
    relationship,
    selected,
    map,
    model
  } = props;
  const {
    blueprint
  } = relationship;
  const clusteringMetadata = attribute.source?.attribute?.metadata?.clustering;
  const groupAttribute = Attributes.getAttribute(clusteringMetadata?.groupAttribute, blueprint.alias);
  const clusterDelegate = getClusterDelegate(relationship, attribute);
  const aggQueue = useRef(new PromiseQueue());
  const dataQueue = useRef(new PromiseQueue());
  const [buckets, setBuckets] = useState([]);
  const [bounds, setBounds] = useState();
  useEffect(() => {
    map.addListener('bounds_changed', () => {
      setBounds(map.getBounds().toJSON());
    });
    aggQueue.current.then(res => {
      const buckets = res.aggs.terms.buckets;
      setBuckets(buckets);
    });
    if (clusterDelegate) {
      GlobalState.dispatch(clusterDelegate.search({
        limit: 10000
      }));
    }
    const hook = (selection, ref) => {
      const parents = selection;
      if (!parents || parents.length === 0 || !ref) {
        return -1;
      }
      const obj = DataSources.getData(ref);
      if (!obj) {
        return -1;
      }
      return parents.findIndex(parent => {
        return parent?.id === groupAttribute.get(obj);
      });
    };
    SelectionResolver.addHook(hook);

    // eslint-disable-next-line consistent-return
    return () => {
      SelectionResolver.removeHook(hook);
    };
  }, []);
  const defaultFilter = useMemo(() => {
    if (!groupAttribute) {
      return DEFAULT_FILTER_MODEL;
    }
    return FilterSourceModel.setFilter(DEFAULT_FILTER_MODEL, {
      type: 'exists',
      field: groupAttribute.source.field,
      not: true
    });
  }, [groupAttribute]);
  const aggTimer = useRef();
  const dataTimer = useRef();
  const getAggregation = useCallback(async () => {
    if (!groupAttribute) {
      return;
    }
    aggQueue.current.push(() => {
      return GlobalState.dispatch(delegate.search({
        limit: 0,
        filter: FilterSourcesModel.getFilter(pFilters),
        aggs: {
          terms: {
            type: 'terms',
            field: groupAttribute.source.field,
            fieldType: 'string',
            missing: MISSING_VALUE
          }
        },
        queryName: `${delegate.queryName}.aggs`
      }));
    });
  }, [pFilters, groupAttribute]);
  const getData = useCallback(async (filters, filter) => {
    const nFilters = {
      type: 'logical',
      operator: 'and',
      conditions: [{
        type: 'logical',
        operator: 'or',
        conditions: [FilterSourceModel.getFilter(defaultFilter)]
      }]
    };
    const f = FilterSourcesModel.getFilter(filters);
    if (f) {
      nFilters.conditions.push(f);
    }
    if (bounds) {
      const b = {
        top: bounds.north,
        left: bounds.west,
        right: bounds.east,
        bottom: bounds.south
      };
      nFilters.conditions.push({
        type: 'geoBoundingBox',
        ...b,
        field: attribute.source?.field
      });
    }
    if (filter) {
      nFilters.conditions[0].conditions.push(FilterSourceModel.getFilter(filter));
    }
    dataQueue.current.push(() => {
      return GlobalState.dispatch(delegate.search({
        limit: 10000,
        filter: nFilters,
        sort: FilterSourcesModel.getSort(filters)
      }));
    });
  }, [defaultFilter, bounds?.south, bounds?.west, bounds?.north, bounds?.east]);
  useEffect(() => {
    clearTimeout(aggTimer.current);
    aggTimer.current = setTimeout(() => {
      getAggregation();
    }, 50);
  }, [getAggregation]);
  useEffect(() => {
    clearTimeout(dataTimer.current);
    dataTimer.current = setTimeout(() => {
      getData(pFilters, filter);
    }, 50);
  }, [getData, pFilters, filter]);
  useEffect(() => {
    if (!groupAttribute) {
      return;
    }
    const sel = Selection.get(GlobalState.get(), 'selected');
    const items = sel?.items;
    const tagGroupIndex = items?.[0]?.type === `${relationship.objectType}-group`;
    if (!tagGroupIndex) {
      onFilterChange({
        value: null
      });
      return;
    }
    MapControl.flyToBounds('map', items[0].features[0].properties.bounds, {
      padding: 60
    });
    const parentId = items[0].id;
    const nFilter = FilterSourceModel.setFilter(filter || DEFAULT_FILTER_MODEL, {
      type: 'equals',
      field: groupAttribute.source.field,
      value: parentId
    });
    onFilterChange({
      value: nFilter
    });
  }, [selected, groupAttribute]);
  const centers = useMemo(() => {
    const options = {
      units: 'meters'
    };
    const selectedItem = selected?.items?.[0];
    const ret = [];
    for (let i = 0; i < buckets.length; i++) {
      const bucket = buckets[i];
      const obj = ItemDelegate.getItem(bucket.key);
      if (!obj) {
        continue;
      }
      const objType = attribute?.source?.attribute?.metadata?.clustering?.groupAttributeObjType;
      const geoShapeAttr = Attributes.getAttribute('geoShape', objType);
      if (!geoShapeAttr) {
        continue;
      }
      // TODO: Support non-geoshape
      const geoshape = JSON.parse(geoShapeAttr.get(obj));
      const box = bbox(geoshape);
      const [minX, minY, maxX, maxY] = box;
      const size = Math.max(distance(point([minX, minY]), point([minX, maxY]), options), distance(point([minX, minY]), point([maxX, minY]), options));
      if (obj) {
        const center = centerOfMass(geoshape);
        const parentId = bucket.key;
        const id = parentId;
        if (id === selectedItem?.id) {
          continue;
        }
        ret.push({
          ...center,
          properties: {
            maxSize: size,
            bounds: [[minX, minY], [maxX, maxY]],
            obj,
            count: bucket.count,
            id,
            dataId: id,
            dataType: `${relationship.objectType}-group`,
            dataRef: delegate.getInterfaceUrl(),
            parentId,
            fillColor: Util.getColor(Model.getFillColor(model), bucket),
            lineColor: Util.getColor(Model.getLineColor(model), bucket),
            labelColor: Util.getColor(Model.getLabelColor(model), bucket),
            opacity: Model.getFillOpacity(model),
            rolloverFillColor: Util.getColor(Model.getRolloverFillColor(model), bucket),
            rolloverLineColor: Util.getColor(Model.getRolloverLineColor(model), bucket),
            rolloverLabelColor: Util.getColor(Model.getRolloverLabelColor(model), bucket),
            rolloverOpacity: Model.getRolloverFillOpacity(model),
            labelSize: Model.getLabelSize(model)
          }
        });
      }
    }
    ret.sort((a, b) => {
      if (a.properties.maxSize < b.properties.maxSize) {
        return 1;
      }
      return -1;
    });
    return ret;
  }, [buckets, selected, attribute, model]);
  return {
    centers
  };
}
function getClusterRelationship(relationship, attr) {
  if (relationship) {
    const srcs = attr?.source?.attribute?.metadata?.clustering?.clusterSources;
    if (srcs) {
      const src = srcs.find(d => d.path === relationship.path);
      if (src && src.sourcePath) {
        let rel = Relationship.getByPath(src.sourcePath);
        if (rel) {
          const {
            match
          } = relationship;
          rel = rel.createResolved(match);
          return {
            relationship: rel,
            match,
            filter: src.filter,
            queryName: `cluster-${src.sourcePath}`
          };
        }
      }
    }
  }
  return null;
}
function getClusterDelegate(relationship, attr) {
  const rel = getClusterRelationship(relationship, attr);
  return rel ? new GroupDelegate({
    ...rel,
    ttl: 30000
  }) : null;
}